import { useEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Skeleton } from '../../platform/async/Skeleton'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { Panel } from '../../platform/primitives/Panel'
import type { Breakpoint } from '../../platform/responsive/useBreakpoint'
import { dayBuckets, narrowedAnnouncement, scopeCountLabel } from './calendarGrid'
import { monthName, narrowSections, SECTION_ORDER, type Section, type SectionKey } from './listSections'
import type { YearMonth } from './monthParam'
import { CalendarGrid, CalendarSkeleton, createLinkStore, monthKey, type GridHandle } from './RecurringCalendar'
import { emitDayOpened } from './recurringAnalytics'
import { formatDue, type LocalDate } from './recurringRules'
import { RecurringSections, type AccountNames, type RowLayout } from './RecurringSections'
import type { Entry } from './recurringWire'
import type { AmountChanges } from './useAmountChanges'

/**
 * The board's arrangement for a breakpoint (rule 5: chosen from
 * `useBreakpoint()`, never a media query). Desktop and wide put the grid
 * beside the list; tablet and phone stack them. The list's rows are cards
 * where its column is too narrow for a `DataGrid` row — on a phone, and
 * beside the grid at desktop (~416–680px) — and `DataGrid` rows otherwise
 * (Ruling 56).
 */
export function boardLayout(bp: Breakpoint): { split: boolean; phone: boolean; rows: RowLayout } {
  const split = bp === 'desktop' || bp === 'wide'
  return { split, phone: bp === 'phone', rows: bp === 'phone' || bp === 'desktop' ? 'cards' : 'grid' }
}

const boardClass = (split: boolean, phone: boolean) => `rec-board ${split ? 'rec-board--split' : 'rec-board--stack'}${phone ? ' rec-board--phone' : ''}`

const HINT_ID = 'rec-cal-hint'

/** The day a grid day number stands for, as a `LocalDate`. */
const dateOf = (ym: YearMonth, day: number): LocalDate => `${monthKey(ym)}-${String(day).padStart(2, '0')}`

function countsOf(sections: Record<SectionKey, Section>): { bills: number; income: number } {
  let bills = 0
  let income = 0
  for (const k of SECTION_ORDER) {
    bills += sections[k].count
    income += sections[k].entries.filter((e) => e.bill.isInflow).length
  }
  return { bills, income }
}

/**
 * Brings the list's scope bar into view after a POINTER activation (never a
 * keyboard one: that would carry the focused day off-screen, WCAG 2.4.11).
 * Always an instant jump: a smooth scroll is motion, and M0-R20 allows no
 * motion outside motion.css.
 * Stacked (`stickyGrid` null), it always scrolls — the list sits below the
 * grid. Beside the grid it scrolls only when the list's top has gone above the
 * sticky grid's top — both start level, so that means the user has scrolled
 * down the list — and a shrunken list would otherwise leave them facing empty
 * page. Its `scroll-margin-top` keeps it clear of the top bar. Not a layout
 * query, so `useBreakpoint` is not its source (rule 5).
 */
function revealScope(el: HTMLElement | null, stickyGrid: Element | null) {
  if (!el || typeof el.scrollIntoView !== 'function') return
  if (stickyGrid && el.getBoundingClientRect().top >= stickyGrid.getBoundingClientRect().top) return
  el.scrollIntoView({ block: 'start', behavior: 'auto' })
}

/**
 * `/recurring`'s month board (#704): the month grid and the month list on ONE
 * screen — side by side at desktop and wide, with the grid sticky while the
 * list scrolls; stacked below that. Presentational: the month's sections and
 * the row actions arrive as props. Mount it with `key` set to the month, so a
 * narrowing belongs to the month it was made in.
 *
 * - **The grid is the index, the list is the ledger.** Activating a busy day
 *   narrows the list to it — a FILTER over the month's already-classified
 *   sections (`narrowSections`), never a second classification (Ruling 30).
 *   The same day again, the scope chip, or Escape restores the month. A
 *   mutation holds the narrowing, so a row marked paid settles in place.
 * - **The tiles do not narrow**: they are month figures, and are the caller's.
 * - **Rows light their day**: hovering or focusing a row outlines its day on
 *   the grid, beside the grid only (stacked, the grid is off-screen).
 * - One live region speaks each narrowing and clearing; `<Async>` announces
 *   only the load, so nothing is said twice.
 */
export function RecurringBoard({
  ym,
  sections,
  today,
  breakpoint,
  accounts,
  changes,
  actions,
}: {
  ym: YearMonth
  sections: Record<SectionKey, Section>
  today: LocalDate
  breakpoint: Breakpoint
  accounts: AccountNames
  changes: AmountChanges
  actions: (e: Entry) => ReactNode
}) {
  const { split, phone, rows } = boardLayout(breakpoint)
  const days = useMemo(() => dayBuckets(sections), [sections])
  const [day, setDay] = useState<number | null>(null)
  const [announced, setAnnounced] = useState('')
  const [links] = useState(createLinkStore)
  const boardRef = useRef<HTMLDivElement>(null)
  const scopeRef = useRef<HTMLDivElement>(null)
  const emptyRef = useRef<HTMLDivElement>(null)
  const gridHandle = useRef<GridHandle>(null)

  const name = monthName(ym)
  const shown = useMemo(() => (day === null ? sections : narrowSections(sections, dateOf(ym, day))), [sections, ym, day])
  const counts = countsOf(shown)
  const short = day === null ? '' : formatDue(dateOf(ym, day), 'short')
  const monthEmpty = SECTION_ORDER.every((k) => sections[k].entries.length === 0)
  const dayEmpty = day !== null && counts.bills === 0 && counts.income === 0

  // A narrowed day can BECOME empty (a Delete removed its last bill). It is
  // not auto-cleared — that would move the list and focus under the user — so
  // the change is spoken instead. Only on the transition, never on arrival.
  const wasEmpty = useRef(dayEmpty)
  useEffect(() => {
    if (dayEmpty && !wasEmpty.current) setAnnounced(narrowedAnnouncement({ bills: 0, income: 0 }, short))
    wasEmpty.current = dayEmpty
  }, [dayEmpty, short])

  // A row removed or moved by a refetch under a still pointer fires no
  // pointerout, so its day would stay lit: new sections start unlinked.
  useEffect(() => links.set(null), [sections, links])

  function clear(refocus: boolean) {
    const was = day
    setDay(null)
    setAnnounced(`Showing all of ${name}`)
    // The chip and "Show all" unmount with the narrowing: their focus goes back to the day they named.
    if (refocus && was !== null) gridHandle.current?.focusDay(was)
  }

  /** Focus is on a control that unmounts when the narrowing clears (the chip, or "Show all"). */
  const focusWillUnmount = () => {
    const active = document.activeElement
    return !!active && (!!scopeRef.current?.contains(active) || !!emptyRef.current?.contains(active))
  }

  function activate(n: number, pointer: boolean) {
    if (n === day) {
      clear(false)
      return
    }
    setDay(n)
    emitDayOpened(days.get(n)?.entries.length ?? 0)
    setAnnounced(narrowedAnnouncement(countsOf(narrowSections(sections, dateOf(ym, n))), formatDue(dateOf(ym, n), 'short')))
    if (pointer) revealScope(scopeRef.current, split ? (boardRef.current?.querySelector('.rec-board__grid') ?? null) : null)
  }

  // Escape anywhere in the board clears a narrowing — unless something inside
  // (an open row menu) already handled it and said so with preventDefault.
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Escape' || e.defaultPrevented || day === null) return
    e.preventDefault()
    clear(focusWillUnmount())
  }

  // Row → day link, delegated: one listener on the list column rather than one
  // per row. Events from the portalled row menu bubble here through React but
  // are not inside the column's DOM, so they are ignored.
  function linkFrom(target: EventTarget, column: HTMLElement) {
    if (!(target instanceof Element) || !column.contains(target)) return
    // A card carries its date itself; a grid row carries it on its lead cell.
    const row = target.closest<HTMLElement>('[role="row"], .rec-card')
    const due = (row?.dataset.due !== undefined ? row : row?.querySelector<HTMLElement>('[data-due]'))?.dataset.due
    links.set(due?.startsWith(`${monthKey(ym)}-`) ? Number(due.slice(8, 10)) : null)
  }
  const linking = split
    ? {
        onPointerOver: (e: PointerEvent<HTMLDivElement>) => linkFrom(e.target, e.currentTarget),
        onPointerLeave: () => links.set(null),
        onFocus: (e: FocusEvent<HTMLDivElement>) => linkFrom(e.target, e.currentTarget),
        onBlur: (e: FocusEvent<HTMLDivElement>) => {
          if (!(e.relatedTarget instanceof Node) || !e.currentTarget.contains(e.relatedTarget)) links.set(null)
        },
      }
    : {}

  return (
    <div ref={boardRef} className={boardClass(split, phone)} onKeyDown={onKeyDown}>
      <Panel className="rec-cal__panel rec-board__grid">
        <CalendarGrid
          key={monthKey(ym)}
          ym={ym}
          days={days}
          today={today}
          selectedDay={day}
          links={links}
          onActivate={activate}
          handle={gridHandle}
          describedBy={day === null ? HINT_ID : undefined}
        />
        {/* Hidden, not removed, once a day is chosen (the chip says it then): the line keeps its height, so nothing moves. */}
        <p id={HINT_ID} className={`rec-cal__hint${day === null ? '' : ' rec-cal__hint--off'}`}>
          Select a day to see its bills
        </p>
      </Panel>
      <div className="rec-board__list" {...linking}>
        <Panel className="rec-sections">
          <div ref={scopeRef} className="rec-scope">
            {day === null ? (
              <p className="rec-scope__label">All of {name}</p>
            ) : (
              <>
                {/* The shared chip Button: neutral, not lime — the brief keeps lime to the month chip and the selected day. */}
                <Button variant="chip" size="panel" aria-label={`Clear ${short} filter`} onClick={() => clear(true)}>
                  {short}
                  <Icon glyph={X} size={12} />
                </Button>
                {!dayEmpty && <span className="rec-scope__count">{scopeCountLabel(counts)}</span>}
              </>
            )}
          </div>
          {monthEmpty ? (
            <p className="rec-month-empty rec-scope__empty">
              Nothing here for {name} {ym.year}.
            </p>
          ) : dayEmpty ? (
            <div ref={emptyRef} className="rec-scope__empty">
              <p className="rec-month-empty">Nothing due {short}.</p>
              {/* It unmounts with the narrowing, so focus returns to the day, as from the chip. */}
              <Button variant="outline" onClick={() => clear(true)}>
                Show all of {name}
              </Button>
            </div>
          ) : (
            <RecurringSections
              sections={shown}
              monthName={name}
              layout={rows}
              today={today}
              accounts={accounts}
              changes={changes}
              actions={actions}
            />
          )}
        </Panel>
      </div>
      <span className="sr-only" aria-live="polite">
        {announced}
      </span>
    </div>
  )
}

/**
 * The board while the month loads (R9): the month-shaped grid skeleton beside
 * (or above) a few skeleton rows, in the board's own arrangement, so nothing
 * moves when the data lands.
 */
export function BoardSkeleton({ ym, breakpoint }: { ym: YearMonth; breakpoint: Breakpoint }) {
  const { split, phone } = boardLayout(breakpoint)
  return (
    <div className={boardClass(split, phone)}>
      <CalendarSkeleton ym={ym} />
      <div className="rec-board__list">
        <Panel className="rec-sections">
          <div className="rec-scope">
            <Skeleton height={10} width={90} />
          </div>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="rec-skeleton-row">
              <Skeleton height={34} width={34} />
              <span className="rec-skeleton-row__text">
                <Skeleton height={13} width="30%" />
                <Skeleton height={10} width="45%" />
              </span>
              <Skeleton height={13} width={70} />
            </div>
          ))}
        </Panel>
      </div>
    </div>
  )
}
